import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import PromptCard from "@/components/PromptCard";

/**
 * Live-filtered result list. Fetches server-side when category is set OR query
 * is >= 2 chars. Falls back to gentle empty state otherwise.
 */
export default function SearchResults({ query, category }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = (query || "").trim();
    if (!q && !category) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/prompts", {
          params: {
            limit: 48,
            search: q || undefined,
            category: category || undefined,
          },
        })
        .then(({ data }) => {
          if (!cancelled) setItems(data);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 220); // debounce for live-typing
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, category]);

  const label = category
    ? `Filtering to ${category}${query ? ` · matching "${query}"` : ""}`
    : `Live matches for "${query}"`;

  return (
    <section
      data-testid="search-results"
      className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10 md:pt-14"
    >
      <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
        <div>
          <div className="font-mono-label text-[#00E5FF]">Search</div>
          <h2 className="font-display text-2xl md:text-4xl tracking-tight mt-1">
            {label}
          </h2>
        </div>
        <span className="font-mono-label text-white/50">
          {loading ? "…" : `${items.length} result${items.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="search-empty"
          className="text-center py-20 border border-dashed border-white/10 rounded-2xl text-white/50"
        >
          No matches. Try a different word or clear the filter.
        </div>
      ) : (
        <div className="masonry" data-testid="search-grid">
          {items.map((p) => (
            <div key={p.id}>
              <PromptCard prompt={p} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
